// ============================================================================
// SHARED ENUMS
// ============================================================================

export enum SourceType {
  Nexset = 'nexset',
  DataSource = 'data_source',
  FlowNode = 'flow_node',
}

export enum HttpMethod {
  Get = 'GET',
  Post = 'POST',
}

export enum ExecutionMode {
  Sync = 'sync',
  Async = 'async',
}

// ============================================================================
// TOOLS TABLE - Stable Identity + Pointers
// ============================================================================

export enum ToolKind {
  NexsetRead = 'nexset_read',
  NexsetAction = 'nexset_action',
  CustomTool = 'custom_tool',
}

export enum ToolStatus {
  Init = 'init',
  Draft = 'draft',
  Published = 'published',
  Paused = 'paused',
  Active = 'active',
  Deprecated = 'deprecated',
  Retired = 'retired',
}

/**
 * Tool entity - direct storage model (versionless)
 * Maps to `tools` table in MCPaaS backend
 */
export type Tool = {
  id: number;
  owner_id: number;
  org_id: number;

  // Resource references
  data_set_id?: number;
  data_source_id?: number;
  flow_node_id?: number;
  origin_node_id?: number;

  key?: string; // e.g., "nexset.378082.read"
  name?: string;
  description?: string;
  tags?: string[];

  kind: ToolKind;
  status: ToolStatus;

  // Tool definition stored directly (no separate versions table)
  tool_definition: Record<string, any>;

  // Hashes for change detection
  contract_hash?: string;
  schema_hash?: string;
  record_schema_fingerprint?: string;

  // Provenance
  generated_from?: Record<string, any>;
  copied_from_id?: number;

  created_at: string;
  updated_at: string;
};

// ============================================================================
// TOOL DEFINITION & HELPER TYPES
// ============================================================================

/**
 * Generated from information - provenance for auto-generated tools
 */
export type GeneratedFrom = {
  source_type: SourceType;
  source_id: number;
  generated_at: string;
  generator_version?: string;
};

/**
 * JSON Schema definition for tool input/output
 */
export type JsonSchema = {
  type: 'object' | 'array' | 'string' | 'number' | 'boolean' | 'integer';
  properties?: Record<string, JsonSchemaProperty>;
  required?: string[];
  $defs?: Record<string, JsonSchema>;
  items?: JsonSchema | { $ref: string };
  additionalProperties?: boolean;
};

export type JsonSchemaProperty = {
  type: string | string[];
  description?: string;
  minimum?: number;
  maximum?: number;
  default?: unknown;
  enum?: unknown[];
  format?: string;
  $ref?: string;
};

/**
 * Binding configuration - how to execute the tool
 */
export enum BindingType {
  NexlaSyncApi = 'NEXLA_SYNC_API',
  NexlaAsyncApi = 'NEXLA_ASYNC_API',
  Webhook = 'WEBHOOK',
}

export type ToolBinding = {
  type: BindingType;
  method: HttpMethod;
  url_template: string;
  timeout_ms: number;
  headers?: Record<string, string>;
};

/**
 * Execution configuration within tool definition
 */
export type ExecutionConfig = {
  mode: ExecutionMode;
  timeout_ms: number;
  bindings: ToolBinding[];
};

export enum LogOutputLevel {
  Full = 'FULL',
  MetadataOnly = 'METADATA_ONLY',
  None = 'NONE',
}

export enum TransformLevel {
  Source = 'SOURCE',
  Transformed = 'TRANSFORMED',
}

/**
 * Tool Definition - the canonical spec now stored directly in tools table
 */
export type ToolDefinition = {
  name: string;
  description: string;
  version: string;

  kind: ToolKind;

  input_schema: JsonSchema;
  output_schema: JsonSchema;

  execution: ExecutionConfig;

  // Governance reference
  governance?: {
    policy_id?: string;
    rate_limit_id?: string;
  };

  // Provenance configuration
  provenance?: {
    receipts_enabled: boolean;
    log_inputs: boolean;
    log_outputs: LogOutputLevel;
  };

  // Lineage information
  lineage?: {
    transform_level: TransformLevel;
    has_custom_transform: boolean;
    source_connector_type?: string;
    source_type?: string;
  };
};

// ============================================================================
// TOOL_CHANGELOGS TABLE - Audit Trail for Tool Changes
// ============================================================================

export enum ToolChangeType {
  Created = 'created',
  DefinitionUpdated = 'definition_updated',
  MetadataUpdated = 'metadata_updated',
  StatusChanged = 'status_changed',
  Deleted = 'deleted',
}

export enum ToolTriggerType {
  UserAction = 'user_action',
  NexsetSync = 'nexset_sync',
  ApiCall = 'api_call',
  System = 'system',
}

/**
 * Field-level change tracking
 */
export type FieldChange = {
  old?: any;
  new?: any;
};

/**
 * Snapshot of hashes before a tool change
 */
export type PreviousHashes = {
  contract_hash?: string;
  schema_hash?: string;
  record_schema_fingerprint?: string;
};

/**
 * Tool Changelog entity - immutable audit trail
 * Maps to `tool_changelogs` table in MCPaaS backend
 */
export type ToolChangelog = {
  id: number;
  tool_id: number;
  owner_id: number;
  org_id: number;

  change_type: ToolChangeType;
  trigger_type: ToolTriggerType;
  trigger_source?: Record<string, any>;

  field_changes: Record<string, FieldChange>;
  previous_definition?: Record<string, any>;
  previous_hashes?: PreviousHashes;

  created_at: string;
};

// ============================================================================
// TOOL_RECEIPTS TABLE - Append-only Receipts
// ============================================================================

export enum ActorType {
  Human = 'human',
  Agent = 'agent',
  Service = 'service',
}

export enum AuthType {
  OAuth = 'oauth',
  OidcJwt = 'oidc_jwt',
  ApiKey = 'api_key',
  Mtls = 'mtls',
}

export enum PolicyDecisionType {
  Allow = 'ALLOW',
  Deny = 'DENY',
  AllowWithRedactions = 'ALLOW_WITH_REDACTIONS',
  AllowWithLimits = 'ALLOW_WITH_LIMITS',
}

export enum ResultStatus {
  Ok = 'OK',
  Error = 'ERROR',
}

/**
 * Receipt JSON - full audit record
 */
export type ReceiptJson = {
  // Request details
  request: {
    arguments: Record<string, unknown>;
    headers?: Record<string, string>;
  };

  // Principal details
  principal: {
    actor_type: ActorType;
    subject_id: string;
    claims?: Record<string, unknown>;
    scopes?: string[];
  };

  // Authentication details
  auth: {
    type: AuthType;
    client_id?: string;
    token_fingerprint?: string;
    token_claims?: Record<string, unknown>;
  };

  // Policy evaluation details
  policy: {
    policy_id: string;
    decision: PolicyDecisionType;
    reason?: string;
    limits?: {
      max_rows?: number;
      max_bytes?: number;
      redacted_fields?: string[];
    };
  };

  // Rate limit info
  rate_limit?: {
    bucket: string;
    consumption: number;
    remaining: number;
    reset_at: string;
  };

  // Execution details
  execution: {
    start_time: string;
    end_time: string;
    duration_ms: number;
    status: ResultStatus;
    row_count?: number;
    bytes_out?: number;
    truncated?: boolean;
    error?: {
      code: string;
      message: string;
    };
  };

  // Data provenance
  data_provenance?: {
    connector_type: string;
    source_type: string;
    dataset_lineage: {
      origin_node_id?: number;
      flow_node_id?: number;
    };
    schema_hash?: string;
    sensitive_handling_flags?: string[];
  };
};

/**
 * Tool Receipt entity
 * Maps to `tool_receipts` table
 */
export type ToolReceipt = {
  id: number;
  receipt_id: string;
  org_id: number;

  tool_fk: number;
  tool_changelog_id?: number; // References changelog entry instead of version

  created_at: string;

  // Principal quick filters
  actor_type: ActorType;
  subject_id: string;

  // Auth quick filters
  auth_type: AuthType;
  client_id?: string;
  token_fingerprint?: string;

  // Policy decision quick filters
  policy_id: string;
  policy_decision: PolicyDecisionType;

  // Trace quick filters
  trace_id: string;

  // Result quick filters
  result_status: ResultStatus;
  row_count?: number;
  truncated?: boolean;

  // Full receipt JSON
  receipt_json: ReceiptJson;
};

// ============================================================================
// TOOLSETS TABLE - Logical Groupings
// ============================================================================

export enum ToolsetStatus {
  Init = 'init',
  Paused = 'paused',
  Active = 'active',
  Retired = 'retired',
}

/**
 * Toolset entity - logical groupings of tools (versionless)
 * Maps to `tool_sets` table in MCPaaS backend
 */
export type Toolset = {
  id: number;
  org_id: number;
  owner_id?: number;

  key?: string;
  name: string;
  description?: string;
  tags?: string[];
  status: ToolsetStatus;

  // MCP server access
  mcp_server_key?: string;
  mcp_url?: string;

  // Manifest stored directly (no separate versions table)
  manifest?: Record<string, any>;
  manifest_hash?: string;
  tools_hash?: string;

  // Compatibility and provenance
  compatibility?: Record<string, any>;
  provenance?: Record<string, any>;

  copied_from_id?: number;

  created_at: string;
  updated_at: string;
};

// ============================================================================
// TOOLSET_TOOLS TABLE - Simple Join Table
// ============================================================================

/**
 * Tool membership in a toolset (simple association, no version selectors)
 * Maps to `tool_sets_tools` table in MCPaaS backend
 */
export type ToolsetTool = {
  id: number;
  owner_id: number;
  org_id: number;
  tool_set_id: number;
  tool_id: number;
  tool_key?: string;
  created_at: string;
  updated_at: string;
};

// ============================================================================
// TOOLSET_CHANGELOGS TABLE - Audit Trail for Toolset Changes
// ============================================================================

export enum ToolsetChangeType {
  Created = 'created',
  ManifestUpdated = 'manifest_updated',
  ToolsAdded = 'tools_added',
  ToolsRemoved = 'tools_removed',
  ToolsChanged = 'tools_changed',
  MetadataUpdated = 'metadata_updated',
  StatusChanged = 'status_changed',
  Deleted = 'deleted',
}

export enum ToolsetTriggerType {
  UserAction = 'user_action',
  ToolChange = 'tool_change',
  ApiCall = 'api_call',
  System = 'system',
}

/**
 * Reference to a tool change within a toolset changelog
 */
export type ToolChangeReference = {
  tool_id: number;
  tool_key?: string;
  changelog_id?: number;
  summary?: string;
};

/**
 * Tool membership changes in a toolset changelog
 */
export type ToolChanges = {
  added: ToolChangeReference[];
  removed: ToolChangeReference[];
  modified: ToolChangeReference[];
};

/**
 * Snapshot of toolset hashes before a change
 */
export type PreviousToolsetHashes = {
  manifest_hash?: string;
  tools_hash?: string;
};

/**
 * Toolset Changelog entity - immutable audit trail
 * Maps to `tool_set_changelogs` table in MCPaaS backend
 */
export type ToolsetChangelog = {
  id: number;
  tool_set_id: number;
  owner_id: number;
  org_id: number;

  change_type: ToolsetChangeType;
  trigger_type: ToolsetTriggerType;
  trigger_source?: Record<string, any>;

  field_changes: Record<string, FieldChange>;
  tool_changes?: ToolChanges;
  previous_manifest?: Record<string, any>;
  previous_hashes?: PreviousToolsetHashes;

  created_at: string;
};

// ============================================================================
// TOOLSET_EXPORTS TABLE - Export Configurations
// ============================================================================

export enum ToolsetExportType {
  McpServer = 'mcp_server',
  CopilotConnector = 'copilot_connector',
  SdkBundle = 'sdk_bundle',
  HttpCatalog = 'http_catalog',
}

export enum ToolsetEnvironment {
  Dev = 'dev',
  Stage = 'stage',
  Prod = 'prod',
}

export enum ToolsetExportStatus {
  Init = 'init',
  Paused = 'paused',
  Active = 'active',
}

/**
 * Export metadata configuration
 */
export type ExportMetadata = {
  // MCP-specific
  mcp_capabilities?: string[];
  mcp_version?: string;

  // Copilot-specific
  copilot_manifest?: Record<string, unknown>;

  // SDK-specific
  sdk_languages?: string[];
  sdk_version?: string;

  // HTTP Catalog-specific
  openapi_spec?: Record<string, unknown>;
};

/**
 * Cached tool entry in tools_cache
 */
export type CachedToolEntry = {
  tool_id: number;
  key: string;
  name?: string;
  description?: string;
  tool_definition: Record<string, any>;
};

/**
 * Cache metadata
 */
export type CacheMetadata = {
  tool_count: number;
  manifest_hash?: string;
  tools_hash?: string;
  generated_at: string;
};

/**
 * Tools cache structure
 */
export type ToolsCache = {
  manifest?: Record<string, any>;
  tools: CachedToolEntry[];
  metadata?: CacheMetadata;
};

/**
 * Toolset Export entity
 * Maps to `tool_set_exports` table in MCPaaS backend
 */
export type ToolsetExport = {
  id: number;
  owner_id: number;
  org_id: number;
  tool_set_id: number; // References toolset directly (no version FK)

  export_type: ToolsetExportType;
  environment: ToolsetEnvironment;
  status: ToolsetExportStatus;

  public_name?: string;
  endpoint_url?: string;
  export_metadata?: ExportMetadata;
  server_key?: string;

  // Cached tool definitions for performance
  tools_cache?: ToolsCache;
  tools_cache_hash?: string;
  cache_generated_at?: string;

  created_at: string;
  updated_at: string;
};

// ============================================================================
// MCP_SESSIONS TABLE - Active MCP Client Sessions
// ============================================================================

/**
 * MCP client information
 */
export type MCPClientInfo = {
  name?: string;
  version?: string;
  platform?: string;
  capabilities?: string[];
};

/**
 * MCP Session entity
 * Maps to `mcp_sessions` table
 */
export type MCPSession = {
  id: number;
  toolset_export_fk: number;
  session_id: string;

  protocol_version?: string;
  initialized: boolean;
  terminated: boolean;

  client_info?: MCPClientInfo;

  created_at: string;
  last_seen_at: string;
};

// ============================================================================
// API Request/Response Types
// ============================================================================

/**
 * Create tool request
 */
export type CreateToolRequest = {
  org_id: number;
  tool_key: string;
  kind: ToolKind;
  name: string;
  description?: string;
  tags?: string[];
  resource_nexset_id?: number;
  resource_data_source_id?: number;
  resource_flow_node_id?: number;
};

/**
 * Update tool request
 */
export type UpdateToolRequest = {
  name?: string;
  description?: string;
  tags?: string[];
  status?: ToolStatus;
  tool_definition?: Record<string, any>;
  contract_hash?: string;
  schema_hash?: string;
  record_schema_fingerprint?: string;
};

/**
 * Generate tools request
 */
export type GenerateToolsRequest = {
  org_id: number;
  selector: {
    nexset_ids?: number[];
    data_source_ids?: number[];
    tags?: string[];
    domains?: string[];
  };
};

/**
 * Generate tools response
 */
export type GenerateToolsResponse = {
  generated_count: number;
  tools: Tool[];
};

/**
 * Create toolset request
 */
export type CreateToolsetRequest = {
  owner_id: number;
  org_id: number;
  key?: string;
  name: string;
  description?: string;
  status?: ToolsetStatus;
  mcp_server_key?: string;
  manifest?: Record<string, any>;
  manifest_hash?: string;
  tools_hash?: string;
  copied_from_id?: number;
};

/**
 * Update toolset request
 */
export type UpdateToolsetRequest = {
  name?: string;
  description?: string;
  status?: ToolsetStatus;
  manifest?: Record<string, any>;
  manifest_hash?: string;
  tools_hash?: string;
};

/**
 * Create toolset export request
 */
export type CreateToolsetExportRequest = {
  owner_id: number;
  org_id: number;
  tool_set_id: number; // References toolset directly (no version FK)
  export_type: ToolsetExportType;
  environment: ToolsetEnvironment;
  status?: ToolsetExportStatus;
  public_name?: string;
  endpoint_url?: string;
  export_metadata?: ExportMetadata;
  server_key?: string;
};

/**
 * Tool call request
 */
export type ToolCallRequest = {
  tool_key: string;
  version?: string; // defaults to latest_published
  arguments: Record<string, unknown>;
  trace_id?: string;
};

/**
 * Tool call response
 */
export type ToolCallResponse = {
  records: Record<string, unknown>[];
  next_cursor?: string;
  receipt_id: string;
  trace_id: string;
};

/**
 * MCP tools/list response item
 */
export type MCPToolListItem = {
  name: string;
  description: string;
  inputSchema: JsonSchema;
};

/**
 * MCP tools/call response
 */
export type MCPToolCallResponse = {
  content: Array<{
    type: 'text';
    text: string;
  }>;
  structuredOutput?: Record<string, unknown>;
  receiptId: string;
};

/**
 * Get tool changelog request
 */
export type GetToolChangelogRequest = {
  tool_id: number;
  limit?: number;
  offset?: number;
};

/**
 * Get toolset changelog request
 */
export type GetToolsetChangelogRequest = {
  toolset_id: number;
  limit?: number;
  offset?: number;
};

// ============================================================================
// Policy & Rate Limit Types (Governance)
// ============================================================================

export enum PolicyEngine {
  Opa = 'opa',
  Cedar = 'cedar',
}

export enum PolicyStatus {
  Active = 'active',
  Disabled = 'disabled',
  Draft = 'draft',
}

export enum PolicyDecision {
  Allow = 'allow',
  Deny = 'deny',
}

/**
 * Policy definition
 */
export type Policy = {
  policy_id: string;
  org_id: number;
  name: string;
  description: string;
  engine: PolicyEngine;
  policy_code: string; // OPA Rego or Cedar policy code
  version: string;
  status: PolicyStatus;
  created_at: string;
  updated_at: string;
};

// ============================================================================
// Governance Types
// ============================================================================

export enum RateLimitScope {
  Principal = 'principal',
  PrincipalTool = 'principal_tool',
  Org = 'org',
  Global = 'global',
}

export type RateLimitConfig = {
  rate_limit_id: string;
  org_id: number;
  name: string;
  scope: RateLimitScope;
  requests_per_minute: number;
  burst: number;
  window_seconds: number;
  status: 'active' | 'disabled';
  created_at: string;
  updated_at: string;
};

// ============================================================================
// Legacy Compatibility Types (for migration)
// ============================================================================

/**
 * @deprecated Use Tool instead
 * Migration: Replace ToolContract with Tool and remove version and nexla_ref fields
 */
export type ToolContract = Tool & { version: string; nexla_ref: unknown };

/** @deprecated Use Toolset instead */
export type ToolPackage = Toolset;

export enum MCPAuthType {
  Bearer = 'bearer',
  ApiKey = 'api_key',
}

export enum ExportKind {
  Mcp = 'mcp',
  DirectApi = 'direct_api',
  Sdk = 'sdk',
}

export enum ReconcileMode {
  ChangedSince = 'changed_since',
  All = 'all',
}

/**
 * @deprecated Use ToolsetExport with export_type='mcp_server' instead
 * Migration: Replace MCPServerConfig with ToolsetExport where export_type === 'mcp_server'
 */
export type MCPServerConfig = {
  endpoint_url: string;
  auth_type: MCPAuthType;
  api_key_id?: string;
};

// Re-export for backwards compatibility
export type PrincipalType = ActorType;
export type UpstreamClient = 'mcp' | 'direct_api' | 'sdk';
export type PolicyDecisionResult = 'allow' | 'deny';
